import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useSnapshot } from "valtio";
import { authStore } from "../../stores/authStore";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "../../components/ui/dropdown-menu";
import {
  ChevronDown,
  UserCircle2Icon,
  LogOut,
  LayoutDashboard,
  BookOpen,
  Users,
  MessageSquare,
  Settings,
  UserIcon,
  Bell,
} from "lucide-react";
import { Sidebar } from "../../components/ui/sidebar";
import SessionExpiryModal from "../../components/dashboard/SessionExpiryModal";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "../../components/ui/breadcrumb";
import {
  COURSE_BUILDER_DRAFT_KEY,
  COURSE_BUILDER_META_KEY,
} from "@/utils/localStoragekeys";
import { NotificationDropdown } from "../../components/ui/notifications";
import logo from "@/assets/logo.svg";
import sponsors from "@/assets/sponsors.svg";
import powered_colored from "@/assets/powered_colored.svg";

const sidebarItems = [
  {
    name: "Dashboard",
    href: "/admin/dashboard",
    icon: <LayoutDashboard className="w-5 h-5" />,
  },
  {
    name: "Manage Users",
    href: "/admin/manage-users",
    icon: <Users className="w-5 h-5" />,
  },
  {
    name: "Manage Courses",
    href: "/admin/manage-courses",
    icon: <BookOpen className="w-5 h-5" />,
  },
  {
    name: "Course Builder",
    href: "/admin/course-builder",
    icon: <BookOpen className="w-5 h-5" />,
    onClick: () => {
      localStorage.removeItem("courseData")
      localStorage.removeItem("topics")
      localStorage.removeItem("assessment")
      localStorage.removeItem("courseBuilderState")
    }
  },
  {
    name: "Manage Tickets",
    href: "/admin/manage-tickets",
    icon: <MessageSquare className="w-5 h-5" />,
  },
  {
    name: "Manage Notifications",
    href: "/admin/manage-notifications",
    icon: <Bell className="w-5 h-5" />,
  },
  {
    name: "Analysis",
    href: "/admin/analysis",
    icon: <LayoutDashboard className="w-5 h-5" />,
  },
];

function getInitials(name) {
  if (!name) return "";
  const words = name.trim().split(" ");
  const initials = words.length >= 2 ? words[0][0] + words[1][0] : words[0][0];
  return initials.toUpperCase();
}

const AdminBreadcrumb = ({ pathname }) => {
  const location = useLocation();
  const state = location.state || {};
  const pathnames = pathname.split("/").filter((x) => x);
  const [nameMap, setNameMap] = useState({});

  // Fetch names for IDs in the path
  useEffect(() => {
    const fetchNames = async () => {
      const newNameMap = {};
      
      for (let i = 0; i < pathnames.length; i++) {
        const segment = pathnames[i];
        const prevSegment = i > 0 ? pathnames[i - 1] : null;
        
        // Check if segment looks like an ID (alphanumeric, long string)
        const isLikelyId = segment && segment.length > 10 && /^[a-zA-Z0-9]+$/.test(segment);
        
        if (isLikelyId) {
          // User ID: /admin/users/view/:id or /admin/users/edit/:id
          if ((prevSegment === "view" || prevSegment === "edit") && pathnames[i - 2] === "users") {
            try {
              const { getUser } = await import("../../api/userApi");
              const response = await getUser(segment);
              if (response?.success && response?.data) {
                const userData = response.data;
                const userName = userData.name || 
                                 `${userData.firstName || ""} ${userData.lastName || ""}`.trim() ||
                                 userData.email?.split("@")[0] ||
                                 "User";
                newNameMap[segment] = userName;
              }
            } catch (error) {
              // Silently fail - will show ID if fetch fails
              console.error("Failed to fetch user name:", error);
            }
          }
          
          // Course ID: /admin/course-builder/:courseId
          if (prevSegment === "course-builder") {
            try {
              const { getCourse } = await import("../../api/courseApi");
              const response = await getCourse(segment);
              if (response?.success && response?.data) {
                const courseData = response.data;
                const courseName = courseData.name || "Course";
                newNameMap[segment] = courseName;
              }
            } catch (error) {
              // Silently fail - will show ID if fetch fails
              console.error("Failed to fetch course name:", error);
            }
          }
        }
      }
      
      if (Object.keys(newNameMap).length > 0) {
        setNameMap(newNameMap);
      }
    };

    fetchNames();
  }, [pathname]);

  // Helper to get display name for breadcrumb segments
  const getDisplayName = (segment, index) => {
    // Skip "view" and "edit" segments - they're just navigation words
    if (segment === "view" || segment === "edit") {
      return null; // Will be handled by showing the name instead
    }
    
    // Check if we have a fetched name for this segment (ID)
    if (nameMap[segment]) {
      return nameMap[segment];
    }
    
    // Check if we have state data for this segment
    if (state.userName && segment.match(/^[a-zA-Z0-9]{20,}$/)) {
      return state.userName;
    }
    
    if (state.courseName && segment.match(/^[a-zA-Z0-9]{20,}$/)) {
      return state.courseName;
    }
    
    // Default formatting: capitalize and replace hyphens
    return segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, " ");
  };

  return (
    <Breadcrumb className="flex items-center gap-0">
      <BreadcrumbList>
        {pathnames.map((name, index) => {
          const routeTo = `/${pathnames.slice(0, index + 1).join("/")}`;
          const isLast = index === pathnames.length - 1;
          const displayName = getDisplayName(name, index);
          
          // Skip "view" and "edit" segments - they're just navigation words
          if (name === "view" || name === "edit") {
            return null;
          }
          
          // If previous segment was "view" or "edit" and current is an ID, use the fetched name
          const prevSegment = index > 0 ? pathnames[index - 1] : null;
          if ((prevSegment === "view" || prevSegment === "edit") && displayName) {
            return (
              <BreadcrumbItem key={name + index}>
                {!isLast ? (
                  <>
                    <BreadcrumbLink asChild>
                      <Link to={routeTo}>
                        {displayName}
                      </Link>
                    </BreadcrumbLink>
                    <BreadcrumbSeparator />
                  </>
                ) : (
                  <>
                    <BreadcrumbLink>
                      {displayName}
                    </BreadcrumbLink>
                  </>
                )}
              </BreadcrumbItem>
            );
          }
          
          return (
            <BreadcrumbItem key={name + index}>
              {!isLast ? (
                <>
                  <BreadcrumbLink asChild>
                    <Link to={routeTo}>
                      {displayName}
                    </Link>
                  </BreadcrumbLink>
                  <BreadcrumbSeparator />
                </>
              ) : (
                <>
                  <BreadcrumbLink>
                    {displayName}
                  </BreadcrumbLink>
                </>
              )}
            </BreadcrumbItem>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
};

const AdminLayout = ({ children }) => {
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [showSessionExpiryModal, setShowSessionExpiryModal] = useState(false);
  const [sessionExpiryTime, setSessionExpiryTime] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useSnapshot(authStore);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/", { state: { from: location }, replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Check if user is admin
  useEffect(() => {
    if (user && user.role !== "ADMIN" && user.role !== "SUPER_ADMIN") {
      navigate("/unauthorized", { replace: true });
    }
  }, [user, navigate]);

  const handleLogout = async () => {
    try {
      await authStore.logout();
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
      // Even if there's an error, we should still redirect to login
      navigate("/");
    }
  };

  return (
    <div className="flex min-h-screen w-full bg-[#f6fafc]">
      {/* Sidebar */}
      <Sidebar
        side="left"
        variant="sidebar"
        collapsible="offcanvas"
        className="w-70 bg-white p-2 shadow-xl font-helvetica text-[#4A4A4A]"
      >
        <div className="p-4 flex flex-col items-start justify-start gap-6">
          <div className="w-fit mx-auto">
            <img src={logo} alt="logo" className="w-26 h-auto" />
          </div>
          <nav className="flex flex-col gap-3 w-full">
            {sidebarItems.map((item) => {
              let isHighlighted = location.pathname
                .toLowerCase()
                .includes(item.href.toLowerCase());
              if (
                location.pathname.toLowerCase().startsWith("/admin/users") &&
                item.name.toLowerCase() === "manage users"
              ) {
                isHighlighted = true;
              }
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={item.onClick}
                  className={`flex items-center gap-3 px-4 py-2 rounded-full transition-all font-semibold text-base ${
                    isHighlighted
                      ? "bg-[#5D9CA4] text-black pointer-events-none"
                      : "text-[#4A4A4A] hover:bg-[#E6F2F4] hover:text-black"
                  }`}
                >
                  <span
                    className={`${
                      isHighlighted ? "text-white" : "text-[#4A4A4A]"
                    }`}
                  >
                    {item.icon}
                  </span>
                  <span
                    className={`${
                      isHighlighted ? "text-white font-bold" : "font-normal"
                    }`}
                  >
                    {item.name}
                  </span>
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="mt-auto flex flex-col items-center gap-2 px-4 py-4">
          <img
            src={sponsors}
            alt="sponsors"
            className="w-full object-contain"
          />
          <img
            src={powered_colored}
            alt="powered"
            className="h-auto w-50 object-contain text-black"
          />
        </div>
      </Sidebar>

      {/* Main content */}
      <main className="flex min-h-[100dvh] min-w-0 flex-1 flex-col bg-[#F4F8F9]">
        {/* Top navigation */}
        <div className="border-b border-[#E6F2F4]">
          <div className="mx-auto w-full px-4 py-2 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <AdminBreadcrumb pathname={location.pathname} />

              <div className="flex items-center space-x-4">
                {/* Enhanced Notifications Component */}
                <NotificationDropdown variant="user" />

                {/* User dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="flex items-center gap-2 px-8 py-6 rounded-md bg-transparent"
                    >
                      {user.profileImage ? (
                        <div className="rounded-md bg-[var(--primary-font)] text-white w-10 h-10 flex items-center justify-center font-bold">
                          <img
                            src={user.profileImage.startsWith("http") ? user.profileImage : import.meta.env.VITE_CDN_LINK + user.profileImage}
                            alt="Profile"
                            className="w-full h-full object-cover object-center rounded-md"
                          />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-full text-sm">
                          <div className="w-full h-full flex items-center justify-center bg-[#5D93A5] rounded-full text-white font-medium text-lg">
                            {getInitials(user.name)}
                          </div>
                        </div>
                      )}
                      <span className="font-semibold text-[15px] font-seoge">
                        {user?.name || "Admin"}
                      </span>
                      <ChevronDown className="w-5 h-5 group-data-[state=open]:rotate-180 transition-transform" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56 p-1" align="end">
                    <DropdownMenuLabel>My Account</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      asChild
                      className="cursor-pointer rounded-md focus:bg-[var(--primary-font)] focus:text-white"
                    >
                      <Link
                        to="/admin/profile"
                        className="flex items-center w-full p-2 rounded-md hover:bg-[var(--primary-font)] hover:text-white transition-colors"
                      >
                        <UserIcon className="mr-2 h-4 w-4" />
                        <span>Profile</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="cursor-pointer text-red-600 rounded-md hover:bg-red-50 focus:bg-red-50 focus:text-red-700"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Log out</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <div className="relative w-full flex-1 overflow-auto bg-[#F4F8F9] px-4 py-4 sm:px-6 md:px-8">
          {children}
        </div>
      </main>

      {/* Session expiry modal */}
      <SessionExpiryModal
        isOpen={showSessionExpiryModal}
        onClose={() => setShowSessionExpiryModal(false)}
        onExtendSession={() => {
          // Handle session extension
          setShowSessionExpiryModal(false);
        }}
        expiryTime={sessionExpiryTime}
      />

      {/* Profile completion modal */}
      {isProfileModalOpen && (
        <ProfileCreationForm closeForm={() => setIsProfileModalOpen(false)} />
      )}
    </div>
  );
};

export default AdminLayout;
